import type * as api from '@gitbook/api-client';
import {
    checkIsNewProductKey,
    compareSiteTypes,
    getMinimumSiteSubscriptionForFeatures,
} from '@gitbook/app-pricing';
import type { BadgeKind } from '@gitbook/spine-react';

import type { UseUpgradeAction } from './types';

/**
 * Maps UseUpgradeAction['kind'] to BadgeKind values where possible.
 */
export function mapUpgradeActionKindToBadgeKind(
    buttonKind: UseUpgradeAction['kind'] | undefined
): BadgeKind | undefined {
    switch (buttonKind) {
        case 'primary':
            return 'upgrade';
        case 'secondary':
        case 'danger':
        case 'upgrade':
            return buttonKind;
    }

    return undefined;
}

/**
 * Check if a site upgrade is required for a site to use the given feature.
 *
 * @param site - The site to check.
 * @param featureKey - The feature to check.
 * @returns True if an upgrade is required, false otherwise.
 */
export function isSiteUpgradeRequiredForFeature({
    organization,
    site,
    featureKey,
}: {
    organization: api.Organization;
    site: api.Site;
    featureKey: api.SiteFeatureKey;
}): boolean {
    const requiredSiteType = getMinimumSiteSubscriptionForFeatures({
        isNewPricing: checkIsNewProductKey(organization.plan),
        featureKeys: [featureKey],
        defaultSiteType: site.type,
    });

    return requiredSiteType ? compareSiteTypes(requiredSiteType, site.type) > 0 : true;
}
